import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, TrendingUp, Upload, X, Calendar, BarChart3, Download, Filter, Images } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { useApp } from '../context/AppContext';
import axios from 'axios';
import CSVExportButton from '../components/CSVExportButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// IB Channels for Revenue
const IB_CHANNELS = [
  'Fira', 'MBE', 'VT Markets', 'XM', 'Exness', 'IC Markets', 'FBS',
  // New Channels from request
  'MSE', 'USTAD PADEI', 'FAIZ ISMAIL', 'EIZLAN YUSOFF', 'CIKO SUPER SCALPER', 'GARUDA', 'REVOLUSI NUSANTARA',
  'Other'
];
const REVENUE_SOURCES = [
  'IB', 'Product', 'Academy', 'Rebate', 'Commission Referral',
  // New Sources from request
  'Sale', 'Subscription Fee', 'Sale Indicator', 'Commission', 'Referral',
  'Other'
];
const COLORS = ['#C41E3A', '#DC143C', '#B22222', '#8B0000', '#A52A2A', '#CD5C5C', '#E9967A', '#F08080'];

// Custom channels storage key
const CUSTOM_CHANNELS_KEY = 'ua_custom_channels';

const formatRM = (amount) => `RM ${(amount || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const getTodayDate = () => new Date().toISOString().split('T')[0];
const getWeekStart = () => {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay());
  return d.toISOString().split('T')[0];
};
const getWeekEnd = () => {
  const d = new Date();
  d.setDate(d.getDate() + (6 - d.getDay()));
  return d.toISOString().split('T')[0];
};
const getMonthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

// Bulk Upload Component
const BulkUploadModal = ({ isOpen, onClose, onUploadComplete }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedUrls, setUploadedUrls] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const addFiles = (newFiles) => {
    const validTypes = ['.jpg', '.jpeg', '.png', '.pdf'];
    const validFiles = newFiles.filter(file => {
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      return validTypes.includes(ext);
    });
    setFiles(prev => [...prev, ...validFiles]);
    setError(null);
  };

  const handleFilesSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Drag and Drop Handlers
  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setError(null);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      
      const response = await axios.post(`${API_URL}/api/upload/bulk`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      const urls = response.data.uploaded.map(f => ({ name: f.original_name, url: f.url }));
      setUploadedUrls(urls);
      
      if (response.data.errors && response.data.errors.length > 0) {
        setError(`${response.data.total_errors} file(s) failed to upload`);
      }
      
      if (urls.length > 0) {
        onUploadComplete(urls);
      }
    } catch (err) {
      console.error('Bulk upload failed:', err);
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setUploadedUrls([]);
    setError(null);
    setUploadProgress(0);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Bulk Upload Images">
      <div className="space-y-4">
        <div 
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
            isDragging ? 'border-red-500 bg-red-50 scale-[1.02]' : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <label className="cursor-pointer">
            <input type="file" accept=".jpg,.jpeg,.png,.pdf" multiple onChange={handleFilesSelect} className="hidden" />
            <Images className={`w-12 h-12 mx-auto mb-2 transition-colors ${isDragging ? 'text-red-500' : 'text-gray-400'}`} />
            <p className={`text-sm font-medium ${isDragging ? 'text-red-600' : 'text-gray-600'}`}>
              {isDragging ? 'Drop files here!' : 'Drag & drop files here'}
            </p>
            <p className="text-xs text-gray-500 mt-1">or click to browse</p>
            <p className="text-xs text-gray-400 mt-2">Supports: JPG, PNG, PDF (Max 10MB each)</p>
          </label>
        </div>

        {files.length > 0 && (
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {files.map((file, idx) => (
              <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                <span className="text-sm text-gray-700 truncate flex-1">{file.name}</span>
                <span className="text-xs text-gray-400 mx-2">{(file.size / 1024).toFixed(1)} KB</span>
                <button onClick={() => removeFile(idx)} className="text-red-500 hover:text-red-700 ml-2">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {uploading && (
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="flex justify-between text-sm text-blue-700 mb-1">
              <span>Uploading...</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }}></div>
            </div>
          </div>
        )}

        {uploadedUrls.length > 0 && (
          <div className="bg-green-50 p-3 rounded-lg">
            <p className="text-sm text-green-700 font-medium">✓ {uploadedUrls.length} files uploaded successfully</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 p-3 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="flex gap-3">
          <button onClick={handleClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button 
            onClick={handleUpload} 
            disabled={files.length === 0 || uploading}
            className="flex-1 px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {uploading ? 'Uploading...' : `Upload ${files.length} Files`}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const RevenueForm = ({ onSubmit, onClose, initialData }) => {
  const [formData, setFormData] = useState(initialData || {
    date: getTodayDate(),
    revenue_source: 'IB',
    channel: '',
    amount: '',
    payment_method: 'Bank Transfer',
    reference: '',
    attachment_url: '',
    attachments: []
  });
  const [uploading, setUploading] = useState(false);
  const [showCustomChannel, setShowCustomChannel] = useState(false);
  const [customChannel, setCustomChannel] = useState('');
  
  // Load custom channels from localStorage
  const [customChannels, setCustomChannels] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(CUSTOM_CHANNELS_KEY)) || [];
    } catch { return []; }
  });
  
  const allChannels = [...IB_CHANNELS, ...customChannels.filter(c => !IB_CHANNELS.includes(c))];
  
  const handleAddCustomChannel = () => {
    if (customChannel.trim() && !allChannels.includes(customChannel.trim())) {
      const newChannels = [...customChannels, customChannel.trim()];
      setCustomChannels(newChannels);
      localStorage.setItem(CUSTOM_CHANNELS_KEY, JSON.stringify(newChannels));
      setFormData({...formData, channel: customChannel.trim()});
      setCustomChannel('');
      setShowCustomChannel(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post(`${API_URL}/api/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ 
        ...formData, 
        attachment_url: response.data.url,
        attachments: [...(formData.attachments || []), { name: file.name, url: response.data.url }]
      });
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ ...formData, amount: parseFloat(formData.amount) });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
        <input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" required />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
          <select value={formData.revenue_source} onChange={(e) => setFormData({...formData, revenue_source: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500">
            {REVENUE_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Channel</label>
          {showCustomChannel ? (
            <div className="flex gap-2">
              <input type="text" value={customChannel} onChange={(e) => setCustomChannel(e.target.value)}
                placeholder="Enter custom channel"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" />
              <button type="button" onClick={handleAddCustomChannel} className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                <Plus className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => setShowCustomChannel(false)} className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <select value={formData.channel} onChange={(e) => setFormData({...formData, channel: e.target.value})}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" required>
                <option value="">-- Select --</option>
                {allChannels.map(ch => <option key={ch} value={ch}>{ch}</option>)}
              </select>
              <button type="button" onClick={() => setShowCustomChannel(true)} 
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200" title="Add custom channel">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Amount (RM)</label>
        <input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" required placeholder="0.00" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
        <select value={formData.payment_method} onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500">
          <option value="Bank Transfer">Bank Transfer</option>
          <option value="Cash">Cash</option>
          <option value="Credit Card">Credit Card</option>
          <option value="Crypto">Crypto</option>
          <option value="Other">Other</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
        <input type="text" value={formData.reference} onChange={(e) => setFormData({...formData, reference: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" placeholder="e.g. INV-001" />
      </div>
      
      {/* Attachment Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Document Attachment</label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
          {formData.attachments && formData.attachments.length > 0 ? (
            <div className="space-y-2">
              {formData.attachments.map((att, idx) => (
                <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                  <span className="text-sm text-gray-600 truncate">{att.name}</span>
                  <button type="button" onClick={() => setFormData({
                    ...formData, 
                    attachments: formData.attachments.filter((_, i) => i !== idx)
                  })} className="text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
                </div>
              ))}
              <label className="cursor-pointer inline-block mt-2">
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileUpload} className="hidden" />
                <span className="text-sm text-red-600 hover:text-red-700">+ Add more</span>
              </label>
            </div>
          ) : (
            <label className="cursor-pointer">
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileUpload} className="hidden" />
              <div className="flex flex-col items-center gap-2">
                <Upload className={`w-8 h-8 ${uploading ? 'text-gray-400 animate-pulse' : 'text-gray-400'}`} />
                <span className="text-sm text-gray-500">{uploading ? 'Uploading...' : 'Click to upload (PDF/Image)'}</span>
              </div>
            </label>
          )}
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
        <button type="submit" disabled={uploading} className="flex-1 px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center justify-center gap-2 disabled:opacity-50">
          <Save className="w-4 h-4" /> Save
        </button>
      </div>
    </form>
  );
};

const RevenuePage = () => {
  const { revenueList, addRevenue, updateRevenue, deleteRevenue, loading } = useApp();
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [period, setPeriod] = useState('monthly');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  
  // Filter states
  const [filterType, setFilterType] = useState('all'); // all, week, month, custom
  const [customDateFrom, setCustomDateFrom] = useState(getMonthStart());
  const [customDateTo, setCustomDateTo] = useState(getTodayDate());
  const [filterChannel, setFilterChannel] = useState('');

  // Fetch analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      setAnalyticsLoading(true);
      try {
        const response = await axios.get(`${API_URL}/api/revenue/analytics?period=${period}`);
        setAnalyticsData(response.data);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setAnalyticsLoading(false);
      }
    };
    fetchAnalytics();
  }, [period, revenueList]);

  // Ensure arrays
  const safeRevenueList = Array.isArray(revenueList) ? revenueList : [];

  // Filter revenue list
  const filteredRevenue = safeRevenueList.filter(item => {
    // Date filter
    if (filterType === 'week') {
      const weekStart = getWeekStart();
      const weekEnd = getWeekEnd();
      if (item.date < weekStart || item.date > weekEnd) return false;
    } else if (filterType === 'month') {
      const monthStart = getMonthStart();
      if (!item.date.startsWith(monthStart.substring(0, 7))) return false;
    } else if (filterType === 'custom') {
      if (item.date < customDateFrom || item.date > customDateTo) return false;
    }
    // Channel filter
    if (filterChannel && item.channel !== filterChannel) return false;
    return true;
  });

  // Prepare chart data
  const chartData = analyticsData?.data?.map(d => ({
    period: d.period,
    amount: d.total
  })) || [];

  // Prepare pie chart data by channel
  const byChannelData = safeRevenueList.reduce((acc, item) => {
    const channel = item.channel || 'Other';
    const existing = acc.find(a => a.name === channel);
    if (existing) existing.value += item.amount || 0;
    else acc.push({ name: channel, value: item.amount || 0 });
    return acc;
  }, []).sort((a, b) => b.value - a.value);

  const handleAdd = async (data) => {
    await addRevenue(data);
    setModalOpen(false);
  };

  const handleEdit = async (data) => {
    await updateRevenue(editingItem.id, data);
    setEditingItem(null);
    setModalOpen(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this revenue entry?')) {
      await deleteRevenue(id);
    }
  };

  const handleBulkUploadComplete = (urls) => {
    console.log('Uploaded files:', urls);
    setBulkUploadOpen(false);
  };

  // Calculate filtered totals
  const filteredTotal = filteredRevenue.reduce((sum, item) => sum + (item.amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Revenue</h1>
          <p className="text-gray-500 text-sm">Manage and track income sources</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Period Selector */}
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-1">
            {['monthly', 'quarterly', 'yearly'].map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  period === p ? 'bg-red-700 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {p === 'monthly' ? 'Monthly' : p === 'quarterly' ? 'Quarterly' : 'Yearly'}
              </button>
            ))}
          </div>
          <button onClick={() => setBulkUploadOpen(true)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2">
            <Images className="w-4 h-4" /> Bulk Upload
          </button>
          <CSVExportButton endpoint="/export/revenue.csv" filename="revenue" testId="export-revenue-csv" />
          <button onClick={() => { setEditingItem(null); setModalOpen(true); }}
            className="px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Revenue
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filter:</span>
          </div>
          
          {/* Date Filter */}
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            {[
              { value: 'all', label: 'All' },
              { value: 'week', label: 'This Week' },
              { value: 'month', label: 'This Month' },
              { value: 'custom', label: 'Custom' }
            ].map(f => (
              <button
                key={f.value}
                onClick={() => setFilterType(f.value)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  filterType === f.value ? 'bg-white text-red-700 shadow-sm' : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Custom Date Range */}
          {filterType === 'custom' && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customDateFrom}
                onChange={(e) => setCustomDateFrom(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={customDateTo}
                onChange={(e) => setCustomDateTo(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          )}

          {/* Channel Filter */}
          <select
            value={filterChannel}
            onChange={(e) => setFilterChannel(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">All Channels</option>
            {IB_CHANNELS.map(ch => <option key={ch} value={ch}>{ch}</option>)}
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            <span className="text-gray-500 text-sm">Total Revenue</span>
          </div>
          <p className="text-xl font-bold text-emerald-600">
            {formatRM(analyticsData?.summary?.total || 0)}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Filter className="w-5 h-5 text-blue-600" />
            <span className="text-gray-500 text-sm">Filtered Total</span>
          </div>
          <p className="text-xl font-bold text-blue-600">
            {formatRM(filteredTotal)}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-5 h-5 text-purple-600" />
            <span className="text-gray-500 text-sm">Entries</span>
          </div>
          <p className="text-xl font-bold text-purple-600">
            {filteredRevenue.length} / {safeRevenueList.length}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="w-5 h-5 text-amber-600" />
            <span className="text-gray-500 text-sm">Top Channel</span>
          </div>
          <p className="text-lg font-bold text-amber-600 truncate">
            {byChannelData.length > 0 ? byChannelData[0]?.name : '-'}
          </p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Trend Chart */}
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4">Revenue Trend</h3>
          {analyticsLoading ? (
            <div className="h-64 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => formatRM(v)} />
                <Area type="monotone" dataKey="amount" stroke="#C41E3A" fill="#C41E3A" fillOpacity={0.2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Pie Chart by Channel */}
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4">Revenue by Channel</h3>
          {byChannelData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={byChannelData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {byChannelData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatRM(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400">
              No channel data available
            </div>
          )}
        </div>
      </div>

      {/* Revenue Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Revenue Records</h3>
          <span className="text-sm text-gray-500">{filteredRevenue.length} entries</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Source</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Channel</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Payment</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredRevenue.slice(0, 50).map(item => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-700">{item.date}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.revenue_source}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    <span className="px-2 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium">
                      {item.channel || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-emerald-600">
                    {formatRM(item.amount)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.payment_method}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => { setEditingItem(item); setModalOpen(true); }}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(item.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredRevenue.length === 0 && (
            <div className="p-8 text-center text-gray-400">No revenue records found</div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal isOpen={modalOpen} onClose={() => { setModalOpen(false); setEditingItem(null); }} 
        title={editingItem ? 'Edit Revenue' : 'Add Revenue'}>
        <RevenueForm 
          onSubmit={editingItem ? handleEdit : handleAdd} 
          onClose={() => { setModalOpen(false); setEditingItem(null); }}
          initialData={editingItem}
        />
      </Modal>

      {/* Bulk Upload Modal */}
      <BulkUploadModal 
        isOpen={bulkUploadOpen} 
        onClose={() => setBulkUploadOpen(false)}
        onUploadComplete={handleBulkUploadComplete}
      />
    </div>
  );
};

export default RevenuePage;
